import { useEffect } from 'react';
import { useForecastMap, mapAlive } from './ForecastMapContext';
import { getOverlay } from '../../lib/overlays/registry';
import { tileTemplate } from '../../lib/overlays/urls';

// Builds the MapLibre fill-color expression: idx -> colorRamp[idx].
const rampPaint = (colorRamp, opacity) => ({
    'fill-color': ['to-color', ['at', ['to-number', ['get', 'idx']], ['literal', colorRamp]]],
    'fill-opacity': opacity,
    'fill-antialias': true,
});

// Skysight's overdevelopment/precip marker: a `symbol` layer drawn from the
// SAME vector tile as the fill, but the `{time}_extra` source-layer. The
// per-feature `z` property gates which zoom a cell's icon appears at (declutter);
// `cc7` is the cloud-with-rain sprite (in our proxied sprite sheet). Empty icon
// name ("") hides non-matching features. Verified live 2026-06-16: proxied tiles
// already carry `_extra`, so no worker change is needed; the overlays that ship
// it today are Height of Thermals (hwcrit), Thermal Strength (wstar_bsratio),
// CAPE/Storms (cape) and Cu Cloudbase (zsfclclmask) — others render no icons.
// Layout is Skysight's captured layer verbatim (the three overlap flags are
// redundant under maplibre 4's `icon-overlap` but kept to mirror the source).
const OVERDEV_ICON_LAYOUT = {
    'icon-image': ['step', ['zoom'],
        ['case', ['<=', ['get', 'z'], 5], 'cc7', ''], 6,
        ['case', ['<=', ['get', 'z'], 6], 'cc7', ''], 7,
        ['case', ['<=', ['get', 'z'], 7], 'cc7', '']],
    'icon-size': 0.7,
    'icon-overlap': 'always',
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
};

// Adds one vector source + one fill layer per registry param for the active
// overlay/time, removing the previous ones. `idPrefix` keeps overlay layers
// grouped and below the launch layer.
const ForecastOverlay = ({ overlayId, region, date, time }) => {
    const map = useForecastMap();

    useEffect(() => {
        if (!map) return undefined;
        const overlay = getOverlay(overlayId);
        if (!overlay) return undefined;

        const effectiveTime = overlay.timeVarying ? time : overlay.fixedTime;
        const sourceLayer = effectiveTime; // Skysight names the source-layer by the time string
        const ids = [];

        overlay.params.forEach((p, i) => {
            const sourceId = `ov:${overlay.id}:${p.name}`;
            // Params may share one tile source (cloud-cover: the CFRACL tile
            // embeds CFRACM/CFRACH as extra source-layers) — layer ids carry
            // the param index so they stay unique.
            const layerId = `${sourceId}:${i}:fill`;
            const extraLayerId = `${sourceId}:${i}:extra`;
            ids.push({ sourceId, layerId, extraLayerId });

            if (!map.getSource(sourceId)) {
                map.addSource(sourceId, {
                    type: 'vector',
                    tiles: [tileTemplate({ region, date, time: effectiveTime, param: p.name })],
                    minzoom: 3,
                    // Skysight serves these tiles only to z5; MapLibre overzooms
                    // for closer views. Setting 6 made it request non-existent z6
                    // tiles (204) and render nothing.
                    maxzoom: 5,
                });
            }
            if (!map.getLayer(layerId)) {
                map.addLayer({
                    id: layerId,
                    type: 'fill',
                    source: sourceId,
                    // The main data layer is named by the time string; embedded
                    // extra layers (e.g. CFRACM/CFRACH) are named by param.
                    'source-layer': p.sourceLayer || sourceLayer,
                    paint: p.paintOverride || rampPaint(p.colorRamp, p.opacity),
                });
            }
            // Overdevelopment/precip icons from the same source's `{time}_extra`
            // source-layer. MapLibre renders nothing if that source-layer has no
            // features, so this self-gates to the overlays that carry it.
            if (!map.getLayer(extraLayerId)) {
                map.addLayer({
                    id: extraLayerId,
                    type: 'symbol',
                    source: sourceId,
                    'source-layer': `${effectiveTime}_extra`,
                    layout: OVERDEV_ICON_LAYOUT,
                });
            }
        });

        return () => {
            if (!mapAlive(map)) return; // map already destroyed (route unmount)
            // All layers first, then sources — params can share a source, and
            // MapLibre refuses to remove a source a layer still references.
            ids.forEach(({ layerId, extraLayerId }) => {
                if (map.getLayer(extraLayerId)) map.removeLayer(extraLayerId);
                if (map.getLayer(layerId)) map.removeLayer(layerId);
            });
            ids.forEach(({ sourceId }) => { if (map.getSource(sourceId)) map.removeSource(sourceId); });
        };
    }, [map, overlayId, region, date, time]);

    return null;
};

export default ForecastOverlay;
